import React, {useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {SequenceTopology} from 'constants/SequenceConstants';
import {
  NormalizedFragment,
  IMMERSIVE_MODAL_PROPS,
  MethodData,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import FragmentsModalContent from 'molbio/bulk-assembly/polymer-map-modals/FragmentsModalContent';
import {fragmentDataToNormalizedFragment} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

export interface FragmentModalProps {
  /** ID of the assembly. Used for analytics. */
  assemblyId: string;
  /** Method metadata for the assembly being performed. */
  bulkAssemblyMethod: MethodData;
  /** Whether the user can specify endpoints for fragments. */
  canSpecifyEndpoints: boolean;
  /** Callback executed when the modal is closed or submitted. */
  onRequestClose: () => void;
  /** Callback to update the selected fragments. Executed when the modal is submitted. */
  onRequestSubmit: (fragments: Array<NormalizedFragment>) => void;
  /** Initial fragments data. */
  initialFragmentsData: Array<FragmentModalFragment>;
  /** Whether the modal should be displayed in a read-only state, e.g. when the assembly is finalized */
  isReadOnly: boolean;
  constructTopology: SequenceTopology;
  numConstructFragments: number;
}

export default function FragmentModal({
  assemblyId,
  bulkAssemblyMethod,
  canSpecifyEndpoints,
  initialFragmentsData,
  onRequestClose,
  onRequestSubmit,
  isReadOnly,
  constructTopology,
  numConstructFragments,
}: FragmentModalProps): JSX.Element {
  const [fragmentsData, setFragmentsData] = useState<Array<FragmentModalFragment>>(initialFragmentsData);
  const actionVerb = isReadOnly ? 'View' : 'Edit';

  return (
    <Modal
      {...IMMERSIVE_MODAL_PROPS}
      title={`${actionVerb} ${pluralize(initialFragmentsData.length, 'fragment')}`}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (!isReadOnly) {
          onRequestSubmit(
            fragmentsData.map((fragmentData) => fragmentDataToNormalizedFragment(fragmentData))
          );
        } else {
          onRequestClose();
        }
      }}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Done', submit: true, style: ButtonStyle.SECONDARY},
      ]}
    >
      <FragmentsModalContent
        assemblyId={assemblyId}
        bulkAssemblyMethod={bulkAssemblyMethod}
        canSpecifyEndpoints={canSpecifyEndpoints}
        fragmentsData={fragmentsData}
        setFragmentsData={setFragmentsData}
        isReadOnly={isReadOnly}
        constructTopology={constructTopology}
        numConstructFragments={numConstructFragments}
      />
    </Modal>
  );
}
